const API_BASE = process.env.REACT_APP_API_URL || '/api';

// In mock mode, we send the user email as a header
// In production, the Azure AD token goes in Authorization
function getAuthHeaders() {
  const token = sessionStorage.getItem('auth_token');
  if (token) {
    return { Authorization: `Bearer ${token}` };
  }
  // Mock mode: default to Jeff for dev
  const mockEmail = localStorage.getItem('mock_user_email') || 'jeff@placeholder.com';
  return { 'X-Mock-User-Email': mockEmail };
}

async function request(path, options = {}) {
  const url = `${API_BASE}${path}`;
  const headers = {
    'Content-Type': 'application/json',
    ...getAuthHeaders(),
    ...options.headers,
  };

  const res = await fetch(url, { ...options, headers });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const err = new Error(body.error || `Request failed: ${res.status}`);
    err.status = res.status;
    throw err;
  }

  return res.json();
}

// --- Contacts ---
export async function getContacts(params = {}) {
  const qs = new URLSearchParams(
    Object.fromEntries(Object.entries(params).filter(([, v]) => v != null && v !== ''))
  ).toString();
  return request(`/contacts${qs ? '?' + qs : ''}`);
}

export async function getContact(id) {
  return request(`/contacts/${id}`);
}

export async function createContact(data) {
  return request('/contacts', { method: 'POST', body: JSON.stringify(data) });
}

export async function updateContact(id, data) {
  return request(`/contacts/${id}`, { method: 'PUT', body: JSON.stringify(data) });
}

export async function deleteContact(id) {
  return request(`/contacts/${id}`, { method: 'DELETE' });
}

// --- Interactions ---
export async function getInteractions(contactId, params = {}) {
  const qs = new URLSearchParams(
    Object.fromEntries(Object.entries(params).filter(([, v]) => v != null && v !== ''))
  ).toString();
  return request(`/contacts/${contactId}/interactions${qs ? '?' + qs : ''}`);
}

export async function createInteraction(contactId, data) {
  return request(`/contacts/${contactId}/interactions`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function getRecentInteractions(limit = 20) {
  return request(`/interactions?limit=${limit}`);
}

// --- Team ---
export async function getTeamMembers() {
  return request('/team-members');
}

// --- User switching (dev only) ---
export function setMockUser(email) {
  localStorage.setItem('mock_user_email', email);
}

export function getMockUser() {
  return localStorage.getItem('mock_user_email') || 'jeff@placeholder.com';
}

// --- Dashboard ---
export async function getDashboardStats(params = {}) {
  const qs = new URLSearchParams(
    Object.fromEntries(Object.entries(params).filter(([, v]) => v != null && v !== ''))
  ).toString();
  return request(`/dashboard${qs ? '?' + qs : ''}`);
}

// --- Deals ---
export async function getDeals(params = {}) {
  const qs = new URLSearchParams(
    Object.fromEntries(Object.entries(params).filter(([, v]) => v != null && v !== ''))
  ).toString();
  return request(`/deals${qs ? '?' + qs : ''}`);
}

export async function getDeal(id) {
  return request(`/deals/${id}`);
}

export async function createDeal(data) {
  return request('/deals', { method: 'POST', body: JSON.stringify(data) });
}

export async function updateDeal(id, data) {
  return request(`/deals/${id}`, { method: 'PUT', body: JSON.stringify(data) });
}

export async function deleteDeal(id) {
  return request(`/deals/${id}`, { method: 'DELETE' });
}

// --- Events ---
export async function getEvents() {
  return request('/events');
}

export async function getEvent(id) {
  return request(`/events/${id}`);
}

export async function createEvent(data) {
  return request('/events', { method: 'POST', body: JSON.stringify(data) });
}

export async function updateEvent(id, data) {
  return request(`/events/${id}`, { method: 'PUT', body: JSON.stringify(data) });
}

export async function deleteEvent(id) {
  return request(`/events/${id}`, { method: 'DELETE' });
}

export async function updateEventMeeting(eventId, contactId, data) {
  return request(`/events/${eventId}/contacts/${contactId}/meeting`, {
    method: 'PUT', body: JSON.stringify(data),
  });
}

export async function removeEventMeeting(eventId, contactId) {
  return request(`/events/${eventId}/contacts/${contactId}/meeting`, { method: 'DELETE' });
}

export async function updateEventTravel(eventId, memberId, data) {
  return request(`/events/${eventId}/travel/${memberId}`, {
    method: 'PUT', body: JSON.stringify(data),
  });
}

// --- Profile ---
export async function getProfileStats(userId) {
  return request(`/profile/${userId}`);
}

// --- Duplicate detection ---
export async function checkDuplicate(params = {}) {
  const qs = new URLSearchParams(
    Object.fromEntries(Object.entries(params).filter(([, v]) => v != null && v !== ''))
  ).toString();
  return request(`/contacts/check-duplicate${qs ? '?' + qs : ''}`);
}

// --- Identity ---
// Fetches current user identity + permissions from server.
// Call once on app load to bootstrap role-based UI.
export async function getMe() {
  return request('/me');
}

// --- Bulk upload ---
export async function bulkUploadContacts(file) {
  const authHeaders = await getAuthHeaders();
  const formData = new FormData();
  formData.append('file', file);
  const res = await fetch(`${API_BASE}/contacts/bulk`, {
    method: 'POST',
    headers: authHeaders, // no Content-Type — browser sets multipart boundary
    body: formData,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const err = new Error(body.error || `Upload failed: ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

export function getBulkUploadTemplateUrl() {
  return `${API_BASE}/contacts/bulk/template`;
}

// --- Team management (admin only) ---
export async function getAllTeamMembers() {
  return request('/team-members/all');
}

export async function updateMemberRole(id, role) {
  return request(`/team-members/${id}/role`, {
    method: 'PATCH', body: JSON.stringify({ role }),
  });
}

export async function deactivateMember(id) {
  return request(`/team-members/${id}/deactivate`, { method: 'PATCH' });
}

export async function reactivateMember(id) {
  return request(`/team-members/${id}/reactivate`, { method: 'PATCH' });
}
